import { zodResolver } from '@hookform/resolvers/zod';
import {
  AccountFormInput,
  AccountSchema,
  AccountStatusEnum,
  AccountTypeEnum,
  IAccountSchema,
  ILegalEntityResponseSchema,
} from '@monetize/types/app';
import { DEFAULT_PAGER } from '@monetize/types/common';
import { FunctionComponent as FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useGetListData } from '../../../../../api/queryUtils';
import {
  ILegalEntitiesSelector,
  selectLegalEntities,
} from '../../../../../api/selectors';
import { useCurrencies } from '../../../../../hooks/useCurrencies';
import { useFlags } from '../../../../../services/launchDarkly';
import AccountForm from '../../../../Accounts/AccountForm';

interface NewAccountFormProps {
  onChange: (account: IAccountSchema) => void;
}

const NewAccountForm: FC<NewAccountFormProps> = ({
  onChange,
}: NewAccountFormProps) => {
  const { allowEditAccountAddress } = useFlags();
  const { defaultCurrency } = useCurrencies();
  const { data: legalEntitiesData } = useGetListData<
    ILegalEntityResponseSchema,
    ILegalEntitiesSelector
  >(
    'legalEntities',
    { config: DEFAULT_PAGER },
    {
      select: selectLegalEntities,
    },
  );

  const form = useForm<AccountFormInput>({
    resolver: zodResolver(AccountSchema),
    mode: 'onChange',
    defaultValues: {
      status: AccountStatusEnum.ACTIVE,
      defaultCurrency,
      defaultDunningProcessId: null,
      isTestAccount: false,
      customFields: {},
      taxEntityUseCode: null,
      taxExemptionNumber: null,
    },
    values: {
      defaultLegalEntityId: legalEntitiesData?.defaultLegalEntity?.id!,
      defaultCurrency,
      defaultDunningProcessId: null,
      isTestAccount: false,
      accountName: '',
      status: AccountStatusEnum.ACTIVE,
      customId: '',
      customFields: {},
      taxEntityUseCode: null,
      taxExemptionNumber: null,
      accountType: AccountTypeEnum.CUSTOMER,
      ccEmails: null,
      bccEmails: null,
    },
  });
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = form;

  const accountName = watch('accountName');
  const isTestAccount = watch('isTestAccount');
  const customId = watch('customId');
  const status = watch('status');
  const currency = watch('defaultCurrency');
  const defaultLegalEntityId = watch('defaultLegalEntityId');
  const customFields = watch('customFields');
  const taxEntityUseCode = watch('taxEntityUseCode');
  const taxExemptionNumber = watch('taxExemptionNumber');
  const defaultDunningProcessId = watch('defaultDunningProcessId');
  const accountType = watch('accountType');
  const ccEmails = watch('ccEmails');
  const bccEmails = watch('bccEmails');

  useEffect(() => {
    onChange({
      accountName,
      isTestAccount,
      customId,
      status,
      defaultCurrency: currency,
      defaultLegalEntityId,
      customFields,
      taxEntityUseCode,
      taxExemptionNumber,
      defaultDunningProcessId,
      accountType: accountType ? accountType : AccountTypeEnum.CUSTOMER,
      ccEmails,
      bccEmails,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountName,
    isTestAccount,
    customId,
    status,
    currency,
    defaultLegalEntityId,
    customFields,
    taxEntityUseCode,
    taxExemptionNumber,
    defaultDunningProcessId,
    accountType,
    ccEmails,
    bccEmails,
  ]);

  return (
    <FormProvider {...form}>
      <AccountForm
        control={control}
        errors={errors}
        setValue={setValue}
        watch={watch}
        legalEntities={legalEntitiesData?.legalEntities}
        allowEditAccountAddress={allowEditAccountAddress}
      />
    </FormProvider>
  );
};

export default NewAccountForm;
